import {
  AssociationType,
  fileAssociationType,
} from "@/shared/domain/association";
import { APIService } from "@/shared/services/ApiService";
import { LoggerService } from "@/shared/services/LoggerService";
import {
  DatasetsCredentialsResponse,
  datasetsCredentialsEndpoint,
  filesUploadCredentialsEndpoint,
} from "@/types";

/**
 * Fetches write credentials for the upload transaction `transactionId` into
 * `associationId`. A dataset's credentials come from the dataset credentials
 * route. An org's, device's or device type's come from
 * `GET /v1/files/upload/<transactionId>/credentials`, which returns a list
 * holding one set of credentials.
 */
export const getFileUploadCredentials = async (
  associationId: string,
  orgId: string,
  transactionId: string,
): Promise<{ response?: DatasetsCredentialsResponse; error?: Error }> => {
  if (fileAssociationType(associationId) !== AssociationType.Dataset) {
    const { response, error } = await APIService.authorizedRequest<{
      data: DatasetsCredentialsResponse["data"][];
    }>({
      method: "GET",
      endpoint: filesUploadCredentialsEndpoint,
      pathParams: { transactionId },
      orgId,
    });

    if (error) {
      LoggerService.error("Error getting credentials for upload", error);
      return { error };
    }

    const credentials = response?.data[0];
    if (!credentials) {
      LoggerService.error("No data returned from credentials endpoint");
      return { error: new Error("No data returned from credentials endpoint") };
    }

    return { response: { data: credentials } };
  }

  const queryParamsObject: { [key: string]: string } = {
    mode: "ReadWrite",
    transaction_id: transactionId,
  };

  const { response, error } =
    await APIService.authorizedRequest<DatasetsCredentialsResponse>({
      method: "GET",
      endpoint: datasetsCredentialsEndpoint,
      pathParams: {
        datasetId: associationId,
      },
      queryParams: new URLSearchParams(queryParamsObject),
      orgId,
    });

  if (error) {
    LoggerService.error("Error getting credentials for dataset upload", error);
    return { error };
  }

  if (!response?.data) {
    LoggerService.error("No data returned from credentials endpoint");
    return { error: new Error("No data returned from credentials endpoint") };
  }

  return { response };
};
